import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { principles, siteInfo, testimonials } from "@/mocks/site";

const aboutJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于我们 - 瓜集社",
    url: "https://yuntuizq.cn/about",
    description: "瓜集社的品牌定位、内容原则与联系方式。",
    inLanguage: "zh-CN",
  },
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: siteInfo.name,
    url: "https://yuntuizq.cn/",
    email: siteInfo.email,
    foundingDate: siteInfo.since,
    description: siteInfo.description,
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://yuntuizq.cn/" },
      { "@type": "ListItem", position: 2, name: "关于我们", item: "https://yuntuizq.cn/about" },
    ],
  },
];

export default function About() {
  usePageMeta({
    title: "关于我们｜瓜集社的内容原则与整理方式",
    description:
      "瓜集社专注把热点话题整理成独家吃瓜合集与独家吃瓜精选专题。这里介绍我们的网站定位、内容原则、用户口碑与联系方式。",
    keywords: "瓜集社,关于我们,内容原则,独家吃瓜精选,合集整理",
    path: "/about",
    jsonLd: aboutJsonLd,
  });

  return (
    <>
      <section className="border-b border-background-200 bg-background-100/50">
        <div className="mx-auto max-w-[1440px] px-4 py-10 md:px-8 md:py-14">
          <nav aria-label="面包屑" className="mb-5 flex items-center gap-2 text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">关于我们</span>
          </nav>
          <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-4xl">
            关于{siteInfo.name}
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {siteInfo.slogan}。我们做的不是热搜搬运，而是把散落的信息整理成可以顺着读完的目录。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-4 py-12 md:px-8 md:py-16">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <span className="text-xs font-medium uppercase tracking-[0.22em] text-primary-600">
              品牌介绍
            </span>
            <h2 className="mt-4 font-heading text-xl font-bold leading-snug text-foreground-950 md:text-3xl">
              从「翻聊天记录」到「打开目录」的转变
            </h2>
            <p className="mt-5 text-sm leading-relaxed text-foreground-600 md:text-base">
              {siteInfo.name}诞生于一个很朴素的困扰：一个话题火起来之后，相关信息散落在各个角落，
              想完整了解总要来回翻找。于是我们开始把这些内容按主题收集起来，整理成一条条有始有终的合集。
            </p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              今天，站内已经收录了上千条独家吃瓜合集与独家吃瓜盘点。每一条专题都标注了整理时间与看点时间线，
              你只需要选择一个话题，就能顺着目录读到结尾。
            </p>
          </div>
          <div className="lg:col-span-6">
            <div className="h-64 w-full overflow-hidden rounded-lg border border-background-200 bg-background-200 md:h-96">
              <img
                src="https://readdy.ai/api/search-image?query=warm%20cozy%20editorial%20desk%20with%20stacked%20magazines%20and%20open%20notebook%2C%20soft%20natural%20light%2C%20warm%20neutral%20tones%2C%20minimal%20composition%2C%20high%20detail&width=1200&height=800&seq=guanji-about-brand-01&orientation=landscape"
                alt="瓜集社内容整理工作台"
                className="h-full w-full object-top"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100/50">
        <div className="mx-auto max-w-[1440px] px-4 py-12 md:px-8 md:py-16">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-3xl">我们的内容原则</h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            四条规则贯穿每一条专题的整理过程，也决定了什么内容会被收录。
          </p>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {principles.map((item) => (
              <div
                key={item.title}
                className="rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className={`${item.icon} text-2xl`} />
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-4 py-12 md:px-8 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-3xl">用户怎么说</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              来自读者的一些真实反馈，也是我们继续整理下去的理由。
            </p>
            <div className="mt-8 rounded-lg border border-background-200 bg-secondary-50 p-5">
              <h3 className="font-heading text-base font-bold text-secondary-900">联系我们</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                对专题内容有疑问、发现信息需要修订，或者想推荐一个值得整理的话题，都欢迎来信。
              </p>
              <a
                href={`mailto:${siteInfo.email}`}
                className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-secondary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-secondary-600"
              >
                <i className="ri-mail-send-line" />
                {siteInfo.email}
              </a>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="space-y-5">
              {testimonials.map((item) => (
                <figure
                  key={item.name}
                  className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
                >
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-100 font-heading text-base font-bold text-accent-700">
                      {item.name.slice(0, 1)}
                    </span>
                    <figcaption className="text-sm">
                      <span className="block font-medium text-foreground-950">{item.name}</span>
                      <span className="block text-xs text-foreground-500">{item.role}</span>
                    </figcaption>
                  </div>
                  <blockquote className="mt-4 text-sm leading-relaxed text-foreground-700">
                    「{item.text}」
                  </blockquote>
                </figure>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-4 pb-16 md:px-8">
        <div className="flex flex-col items-center justify-between gap-6 rounded-lg bg-foreground-950 px-6 py-10 text-center md:flex-row md:px-12 md:text-left">
          <div>
            <h2 className="font-heading text-xl font-bold text-background-50 md:text-2xl">
              先从一条合集开始
            </h2>
            <p className="mt-2 text-sm text-background-300">
              浏览专题目录，找到你感兴趣的话题，一次把它看完。
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Link
              to="/topics"
              className="inline-flex h-11 items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              浏览专题合集
            </Link>
            <Link
              to="/rankings"
              className="inline-flex h-11 items-center justify-center whitespace-nowrap rounded-md border border-background-700 px-6 text-sm font-medium text-background-100 transition-colors hover:border-primary-400 hover:text-primary-300"
            >
              查看盘点榜单
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}